import { MergeBehavior, FieldType, FieldConfig } from "@/types/merge";

/**
 * Utility functions for field type detection and merge behaviors
 */
export class MergeUtils {
	/**
	 * Detect field type based on value content
	 */
	static detectFieldType(value: any): FieldType {
		if (value === null || value === undefined) return 'text';
		
		if (Array.isArray(value)) return 'array';
		if (typeof value === 'boolean') return 'boolean';
		if (typeof value === 'number') return 'number';
		if (typeof value === 'object') return 'json';
		
		if (typeof value === 'string') {
			// Email detection
			if (value.includes('@') && value.includes('.')) return 'email';
			
			// Phone detection (basic)
			if (/^[\+]?[0-9\-\s\(\)]+$/.test(value) && value.length > 5) return 'phone';
			
			// URL detection
			if (value.startsWith('http://') || value.startsWith('https://')) return 'url';
			
			// Date detection
			if (!isNaN(Date.parse(value))) return 'date';
			
			return 'text';
		}
		
		return 'text';
	}

	/**
	 * Apply merge behavior to a field
	 */
	static applyMergeBehavior<T>(
		fieldName: string,
		behavior: MergeBehavior,
		items: T[],
		fieldType: FieldType
	): any {
		const values = items.map(item => item[fieldName]).filter(v => v !== null && v !== undefined && v !== '');
		
		if (values.length === 0) return null;
		if (values.length === 1) return values[0];
		
		switch (behavior) {
			case 'latest':
				return values[values.length - 1];
			
			case 'first':
				return values[0];
			
			case 'longest':
				if (fieldType === 'text') {
					return values.reduce((longest, current) => 
						String(current).length > String(longest).length ? current : longest
					);
				}
				return values[0];
			
			case 'shortest':
				if (fieldType === 'text') {
					return values.reduce((shortest, current) => 
						String(current).length < String(shortest).length ? current : shortest
					);				}				return values[0];
			
			case 'combine':
				if (fieldType === 'array') {
					const combined = values.flat();
					return [...new Set(combined)]; // Remove duplicates
				}
				if (fieldType === 'text') {
					return values.join(' | ');
				}
				if (fieldType === 'presence') {
					// For presence fields, return the first non-empty value
					// as these are typically complex objects that shouldn't be concatenated
					return values[0];
				}
				return values[0];
			
			case 'ai':
				// AI merge also uses combine logic
				if (fieldType === 'array') {
					const combined = values.flat();
					return [...new Set(combined)]; // Remove duplicates
				}
				if (fieldType === 'text') {
					return values.join(' | ');
				}
				if (fieldType === 'presence') {
					// For presence fields, return the first non-empty value
					// as these are typically complex objects that shouldn't be concatenated
					return values[0];
				}
				return values[0];
			
			case 'manual':
			default:
				return values[0];
		}
	}

	/**
	 * Get suggested merge behavior based on field type and name
	 */
	static getSuggestedMergeBehavior(fieldName: string, fieldType: FieldType): MergeBehavior {
		// System fields
		if (['created_at', 'updated_at'].includes(fieldName)) return 'latest';
		if (['id'].includes(fieldName)) return 'first';
		
		// Field type based suggestions
		switch (fieldType) {
			case 'array':
				return 'combine';
			case 'date':
				return 'latest';
			case 'email':
			case 'phone':
				return 'longest';
			case 'text':
				// AI merge for complex text fields
				if (['description', 'notes', 'ai_analysis', 'ai_synthesis'].includes(fieldName)) {
					return 'ai';
				}
				return 'longest';
			default:
				return 'manual';
		}
	}

	/**
	 * Check if field should have AI merge option
	 */
	static shouldEnableAIMerge(fieldName: string, fieldType: FieldType): boolean {
		const aiEnabledFields = [
			'description', 'notes', 'ai_analysis', 'ai_synthesis', 
			'summary', 'content', 'bio', 'experience', 'strengths', 'weaknesses'
		];
		
		return fieldType === 'text' && aiEnabledFields.some(field => 
			fieldName.toLowerCase().includes(field.toLowerCase())
		);
	}	/**
	 * Get display value for field
	 */
	static getDisplayValue(value: any, fieldType: FieldType, fieldName?: string): string | { type: 'document', value: string } {
		if (value === null || value === undefined) return '';
		
		// Always check if value is an object first, regardless of detected fieldType
		if (typeof value === 'object' && !Array.isArray(value)) {
			return { type: 'document', value: JSON.stringify(value, null, 2) };
		}
		
		switch (fieldType) {
			case 'array':
				return Array.isArray(value) ? value.join(', ') : String(value);
			case 'json':
				// This should already be handled above, but keeping for consistency
				return { type: 'document', value: typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value) };
			case 'boolean':
				return value ? 'Yes' : 'No';
			case 'date':
				return new Date(value).toLocaleDateString();
			default:
				// Handle special case for default_resume field - return document indicator
				if (fieldName === 'default_resume' && value && typeof value === 'string') {
					return { type: 'document', value: value };
				}
				return String(value);
		}
	}

	/**
	 * Parse value from string based on field type
	 */
	static parseValue(value: string, fieldType: FieldType): any {
		if (!value) return null;
		
		switch (fieldType) {
			case 'array':
				return value.split(',').map(v => v.trim()).filter(v => v);
			case 'json':
				try {
					return JSON.parse(value);
				} catch {
					return value;
				}
			case 'boolean':
				return value.toLowerCase() === 'true' || value === '1' || value.toLowerCase() === 'yes';
			case 'number':
				return Number(value);
			case 'date':
				return new Date(value).toISOString();
			default:
				return value;
		}
	}
}
